import { ref } from "vue";
import { useAuth } from "./useAuth";
import { genericNetworkError, readUserFacingError } from "../errorMessages";

export type UploadState =
  | "idle"
  | "creating"   // POST /api/jobs
  | "signing"    // POST /api/jobs/:id/upload-url
  | "uploading"  // PUT to B2 (the long step)
  | "finalizing" // POST /api/jobs/:id/uploaded
  | "completed"
  | "cancelled"  // user-initiated abort; distinct from "error"
  | "error";

/**
 * 5 GB (decimal) — matches the single-PUT presigned URL cap that S3/B2
 * advertise as "5 GB". The cap is 5 * 10^9 bytes, NOT 5 GiB (5 * 2^30 =
 * 5.37 GB). Using the binary value here would silently pass files in the
 * 5.0–5.37 GB range past the local check, only for B2 to reject the PUT
 * with no obvious cause.
 */
export const MAX_UPLOAD_BYTES = 5 * 1000 * 1000 * 1000;

/**
 * Shape check for YouTube URLs accepted by the URL-ingest path
 * (Phase 2). Matches the server-side regex in
 * `apps/segment_api/app.py:_YOUTUBE_URL_RE` — keep them in lockstep.
 * Rejects youtube.com/shorts/ and /live/ which don't fit the
 * segmentation pipeline.
 */
const YOUTUBE_URL_RE =
  /^https?:\/\/(?:www\.|m\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)[A-Za-z0-9_-]{11}/;

export function isYouTubeUrl(url: string): boolean {
  return YOUTUBE_URL_RE.test(url.trim());
}

export interface UploaderHandle {
  /** Resolves when B2 has accepted the PUT (2xx). Rejects on network/HTTP error. */
  promise: Promise<void>;
  /** Best-effort abort. Safe to call after promise settles. */
  abort: () => void;
}

export type UploaderFn = (
  url: string,
  file: File,
  onProgress: (sent: number, total: number) => void,
) => UploaderHandle;

/**
 * Typed error thrown by the default uploader when `cancel()` is called.
 * Lets `start()` distinguish a user-initiated cancel from a browser-
 * initiated abort (page navigation, tab close — fires xhr.onabort
 * without our `aborted` flag set). `instanceof` is safer than a string-
 * sentinel marker — wrapped errors can't accidentally masquerade.
 */
export class UploadCancelledError extends Error {
  constructor() {
    super("Upload cancelled.");
    this.name = "UploadCancelledError";
  }
}

/**
 * Internal typed errors propagated through `start()`'s outer try/catch.
 * They replace earlier string-sentinel patterns (`UPLOAD_CANCELLED_MARKER`
 * was already converted to `UploadCancelledError`; "Superseded" return
 * strings and the silent 401 handling now use these instead).
 */
class SupersededError extends Error {
  constructor() {
    super("Upload superseded by reset() or a new start() call.");
    this.name = "SupersededError";
  }
}

class SessionExpiredError extends Error {
  constructor() {
    super("Session expired.");
    this.name = "SessionExpiredError";
  }
}

/**
 * Default uploader uses XMLHttpRequest because `fetch` does not expose
 * upload progress events. Tests inject a stub `UploaderFn` instead of
 * mocking XHR; the default is exported for the smoke tests in this file.
 *
 * `xhr.open` and `xhr.send` can throw synchronously (malformed URL,
 * wrong-state errors). Those throws would otherwise surface as raw
 * browser messages — catch them here and reject with a contextual
 * prefix so `error.value` reads "Failed to start upload to B2: ..."
 * instead of "Failed to construct 'XMLHttpRequest'".
 */
export const xhrUploader: UploaderFn = (url, file, onProgress) => {
  const xhr = new XMLHttpRequest();
  let aborted = false;
  const promise = new Promise<void>((resolve, reject) => {
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(file.size, file.size);
        resolve();
        return;
      }
      // Never render the response body to the user (C-S8 — see
      // docs/design/sync-channel-leak-fix-one-pager.md). B2's error
      // bodies are operator-grade content (XML <Error>...</Error>
      // typically, but contract isn't ours to enforce). Log to console
      // for dev visibility; the rejected error message carries only
      // the status code.
      const raw = (xhr.responseText ?? "").slice(0, 200);
      if (raw) {
        // eslint-disable-next-line no-console
        console.warn(
          `B2 PUT ${xhr.status} body (not shown to user): ${raw}`,
        );
      }
      reject(new Error(`Upload failed (${xhr.status}). Please try again.`));
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.onabort = () =>
      reject(aborted ? new UploadCancelledError() : new Error("Upload aborted"));

    try {
      xhr.open("PUT", url);
      xhr.send(file);
    } catch (err) {
      reject(new Error(`Failed to start upload to B2: ${(err as Error).message}`));
    }
  });
  return {
    promise,
    abort: () => {
      aborted = true;
      xhr.abort();
    },
  };
};

export interface UploadDeps {
  fetchFn?: typeof fetch;
  uploader?: UploaderFn;
}

/**
 * Server-supplied pointer to the prior completed job for the same
 * ``(user_id, youtube_video_id)``. Returned in the 409
 * YOUTUBE_DUPLICATE body's RFC 7807 extension members. See
 * ``docs/design/youtube-reingest-dedupe-one-pager.md``.
 */
export interface DuplicatePointer {
  existingJobId: string;
  existingJobName: string;
  /** ISO-8601 from the server; rendered as a human date in the modal. */
  existingCreatedAt: string;
}

/**
 * Discriminated result type from `start()` / `startWithUrl()`.
 * Cancellation is signalled via `state.value === "cancelled"`;
 * session-expired is signalled by the SIGNED_OUT_EVENT (caller sees
 * `ok: false; error: "Session expired"` but is about to be routed to
 * /login). ``duplicate`` is URL-ingest-only — when set, the SPA
 * renders a dedicated modal rather than the generic error toast.
 * Callers MUST check ``result.duplicate`` before falling through to
 * ``result.error`` (which carries a generic placeholder in that
 * branch so existing render sites keep working without narrowing).
 */
export type StartResult =
  | { ok: true; jobId: string }
  | { ok: false; error: string; duplicate?: DuplicatePointer };

export function useUpload(deps: UploadDeps = {}) {
  const fetchFn = deps.fetchFn ?? globalThis.fetch.bind(globalThis);
  const uploader = deps.uploader ?? xhrUploader;

  const state = ref<UploadState>("idle");
  const progress = ref(0);
  const bytesSent = ref(0);
  const totalBytes = ref(0);
  const bytesPerSec = ref(0);
  const error = ref<string | null>(null);
  // Sibling to `error` — the operator-pasteable Jaeger trace_id when
  // the backend supplied one. NewJobForm renders this under the error
  // toast via the TraceReference component. See
  // ``docs/design/error-correlation-one-pager.md``.
  const errorTraceId = ref<string | undefined>(undefined);
  const jobId = ref<string | null>(null);
  let activeUpload: UploaderHandle | null = null;

  /**
   * Generation counter. `reset()`, `start()`, and `cancel()` each bump it.
   * Every `await` inside `start()` is wrapped in `guardedAwait`, which
   * throws `SupersededError` when the captured `gen` no longer matches.
   * The throw-based pattern (vs explicit `if (superseded()) return` at
   * each await) means adding a new step to `start()` automatically gets
   * supersession-correctness as long as you keep `guardedAwait` wrapping
   * — there's no per-await check to forget.
   */
  let currentGen = 0;

  /**
   * Throughput tracking: smoothed instantaneous rate. The 100ms gate
   * ignores rapid-fire events. EMA blends 0.3 of the instant rate with
   * 0.7 of the smoothed history.
   *
   * The first eligible sample is skipped because its `dtSec` measures
   * the gap from step-3 setup to the first progress event — that gap
   * conflates connection setup with transfer time, producing an inflated
   * cumulative-bytes-divided-by-time rate (e.g. "999 MB/s") for one tick
   * before the EMA stabilizes.
   */
  let lastSampleMs = 0;
  let lastSampleBytes = 0;
  let smoothedRate = 0;
  let hasFirstSample = false;

  function reset(): void {
    currentGen++;
    if (activeUpload) {
      activeUpload.abort();
    }
    state.value = "idle";
    progress.value = 0;
    bytesSent.value = 0;
    totalBytes.value = 0;
    bytesPerSec.value = 0;
    error.value = null;
    errorTraceId.value = undefined;
    jobId.value = null;
    activeUpload = null;
    lastSampleMs = 0;
    lastSampleBytes = 0;
    smoothedRate = 0;
    hasFirstSample = false;
  }

  /**
   * Inner request helper. On 401, clears local auth via
   * `useAuth().signOut()` (which dispatches SIGNED_OUT_EVENT; App.vue's
   * listener routes to /login) and **throws `SessionExpiredError`**. The
   * outer try/catch in `start()` catches it and returns silently
   * (without setting `state.value = "error"` and without flashing a
   * misleading "Upload failed: Session expired" banner before the route
   * change). Mirrors how `useJobs.loadJobs()` and `JobDetailView.load()`
   * treat 401.
   */
  async function apiCall(
    url: string,
    init: RequestInit,
  ): Promise<
    | { ok: true; resp: Response }
    | { ok: false; message: string; traceId?: string }
  > {
    let resp: Response;
    try {
      resp = await fetchFn(url, { credentials: "include", ...init });
    } catch (err) {
      // Network failure (CORS, DNS, fetch threw): no Response, so no
      // traceId to extract. Render message only.
      return { ok: false, message: `Cannot reach backend: ${(err as Error).message}` };
    }
    if (resp.status === 401) {
      const { signOut } = useAuth();
      await signOut();
      throw new SessionExpiredError();
    }
    if (!resp.ok) {
      // C-S8 (sync-channel separation): render USER_MESSAGES[code]
      // for Problem Details bodies, generic-by-status otherwise.
      // NEVER the raw response body — see
      // docs/design/sync-channel-leak-fix-one-pager.md.
      const ufe = await readUserFacingError(resp);
      return { ok: false, message: ufe.message, traceId: ufe.traceId };
    }
    return { ok: true, resp };
  }

  /**
   * Parse a JSON response defensively. A 2xx with malformed body
   * (gateway truncation, hot-path bug returning empty string with
   * `Content-Type: application/json`) would otherwise throw from
   * `.json()` and escape `start()` as an unhandled rejection, leaving
   * the UI stuck on a non-error mid-state.
   *
   * The user-facing `message` is intentionally generic — the JS
   * parser's exception text ("Unexpected token '<' at position 0")
   * is implementation detail and would leak under C-S8. The technical
   * detail is logged for dev visibility instead.
   */
  async function parseJson<T>(
    resp: Response,
    label: string,
  ): Promise<{ ok: true; data: T } | { ok: false; message: string }> {
    try {
      const data = (await resp.json()) as T;
      return { ok: true, data };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        `parseJson failed from ${label} (not shown to user):`,
        (err as Error).message,
      );
      return {
        ok: false,
        message: "Unexpected response from server. Please try again.",
      };
    }
  }

  /**
   * Terminal-state helpers. Centralizing them is what makes the
   * `state === "error"` ⇔ `error.value !== null` and
   * `state === "cancelled"` ⇔ (no error banner) invariants hold without
   * the type system enforcing them. **Never assign `state.value` or
   * `error.value` directly elsewhere — go through these (or `cancel()`,
   * which is the public-API equivalent of `failCancelled`).**
   */
  function failWith(message: string, traceId?: string): StartResult {
    error.value = message;
    errorTraceId.value = traceId;
    state.value = "error";
    activeUpload = null;
    return { ok: false, error: message };
  }

  function failCancelled(): StartResult {
    error.value = null;
    errorTraceId.value = undefined;
    state.value = "cancelled";
    activeUpload = null;
    return { ok: false, error: "Upload cancelled." };
  }

  async function start(file: File, jobName: string): Promise<StartResult> {
    reset();
    const gen = ++currentGen;

    // Wrap every await in start() with this helper. If reset()/cancel()/
    // a new start() bumps `currentGen` while we're awaiting, the helper
    // throws SupersededError and the outer try/catch handles it. Checks
    // supersession on BOTH resolve and reject so that, e.g., a stale
    // UploadCancelledError from a reset-triggered XHR abort gets
    // converted to SupersededError (the resetter already set the
    // terminal state — failCancelled would clobber it). Adding a new
    // await without `guardedAwait` is the regression to watch for.
    const guardedAwait = <T>(p: Promise<T>): Promise<T> =>
      p.then(
        (v) => {
          if (gen !== currentGen) throw new SupersededError();
          return v;
        },
        (err) => {
          if (gen !== currentGen) throw new SupersededError();
          throw err;
        },
      );

    totalBytes.value = file.size;

    try {
      // Step 1: create the Job
      state.value = "creating";
      const create = await guardedAwait(
        apiCall("/api/jobs", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ job_name: jobName, video_filename: file.name }),
        }),
      );
      if (!create.ok) return failWith(create.message, create.traceId);

      const createJson = await guardedAwait(
        parseJson<{ job_id?: unknown }>(create.resp, "POST /jobs"),
      );
      if (!createJson.ok) return failWith(createJson.message);
      const newJobId = createJson.data.job_id;
      if (typeof newJobId !== "string" || newJobId.length === 0) {
        return failWith("Backend response from POST /jobs is missing job_id.");
      }
      jobId.value = newJobId;

      // Step 2: get a presigned PUT URL
      state.value = "signing";
      const sign = await guardedAwait(
        apiCall(`/api/jobs/${newJobId}/upload-url`, { method: "POST" }),
      );
      if (!sign.ok) return failWith(sign.message, sign.traceId);

      const signJson = await guardedAwait(
        parseJson<{ url?: unknown }>(sign.resp, "POST /upload-url"),
      );
      if (!signJson.ok) return failWith(signJson.message);
      const signedUrl = signJson.data.url;
      if (typeof signedUrl !== "string" || signedUrl.length === 0) {
        return failWith("Backend response from POST /upload-url is missing url.");
      }

      // Step 3: PUT the file directly to B2 with progress events
      state.value = "uploading";
      lastSampleMs = Date.now();
      lastSampleBytes = 0;
      smoothedRate = 0;
      hasFirstSample = false;
      activeUpload = uploader(signedUrl, file, (sent, total) => {
        // Stray progress events after reset()/cancel()/new-start() must
        // not clobber the post-supersession state.
        if (gen !== currentGen) return;
        bytesSent.value = sent;
        progress.value = total > 0 ? Math.round((sent / total) * 100) : 0;

        const now = Date.now();
        const dtSec = (now - lastSampleMs) / 1000;
        if (dtSec >= 0.1) {
          if (!hasFirstSample) {
            hasFirstSample = true;
          } else {
            const instantRate = (sent - lastSampleBytes) / dtSec;
            smoothedRate =
              smoothedRate === 0
                ? instantRate
                : 0.3 * instantRate + 0.7 * smoothedRate;
            bytesPerSec.value = Math.max(0, Math.round(smoothedRate));
          }
          lastSampleMs = now;
          lastSampleBytes = sent;
        }
      });
      try {
        await guardedAwait(activeUpload.promise);
      } catch (err) {
        if (err instanceof SupersededError) throw err;
        if (err instanceof UploadCancelledError) return failCancelled();
        return failWith((err as Error).message);
      }
      activeUpload = null;

      // Step 4: tell the backend the upload is done; triggers RunPod
      state.value = "finalizing";
      const finalize = await guardedAwait(
        apiCall(`/api/jobs/${newJobId}/uploaded`, { method: "POST" }),
      );
      if (!finalize.ok) return failWith(finalize.message, finalize.traceId);

      state.value = "completed";
      return { ok: true, jobId: newJobId };
    } catch (err) {
      // SupersededError: reset()/cancel()/new-start() already set the
      // terminal state. Don't mutate it from this stale invocation.
      if (err instanceof SupersededError) {
        return { ok: false, error: "Superseded" };
      }
      // SessionExpiredError: signOut() already fired SIGNED_OUT_EVENT;
      // App.vue's listener is about to route to /login. Stay silent —
      // don't flash an "Upload failed: Session expired" banner before
      // the route change. The form's `isUploading` is computed from
      // state, which remains at the pre-401 phase; Submit stays
      // disabled until the unmount.
      if (err instanceof SessionExpiredError) {
        activeUpload = null;
        return { ok: false, error: "Session expired" };
      }
      throw err;
    }
  }

  /**
   * URL-ingest variant of start(). Posts to POST /jobs with
   * source_url instead of video_filename; the server triggers RunPod
   * inline and returns status="processing". The SPA never sees the
   * upload or finalize phases for URL jobs. See
   * docs/design/youtube-url-ingestion-phase-2-one-pager.md.
   */
  async function startWithUrl(
    sourceUrl: string,
    jobName: string,
    forceNew = false,
  ): Promise<StartResult> {
    reset();
    const gen = ++currentGen;

    const guardedAwait = <T>(p: Promise<T>): Promise<T> =>
      p.then(
        (v) => {
          if (gen !== currentGen) throw new SupersededError();
          return v;
        },
        (err) => {
          if (gen !== currentGen) throw new SupersededError();
          throw err;
        },
      );

    try {
      state.value = "creating";
      // Omit job_name when empty so the server's YouTube-title probe
      // fills the default. Sending an empty string would be treated as
      // a user-supplied name and skip the probe. See
      // docs/design/youtube-job-name-from-title-one-pager.md.
      // ``force_new`` is set by the dedupe modal's "Run again" branch
      // (see docs/design/youtube-reingest-dedupe-one-pager.md). Default
      // false matches the server's request schema.
      const body: Record<string, unknown> = { source_url: sourceUrl };
      const trimmedName = jobName.trim();
      if (trimmedName.length > 0) {
        body.job_name = trimmedName;
      }
      if (forceNew) {
        body.force_new = true;
      }

      // The create-job POST has three success/failure shapes:
      //   201 → happy path
      //   409 + code=YOUTUBE_DUPLICATE → dedupe modal (custom UX)
      //   anything else → fall through to apiCall's generic handling
      // We can't use ``apiCall`` directly for the dedupe branch because
      // it consumes the body via readUserFacingError before the caller
      // sees the response, so the existing_job_* extension members
      // would be lost. Inline the request for this step instead.
      let resp: Response;
      try {
        resp = await guardedAwait(
          fetchFn("/api/jobs", {
            credentials: "include",
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }),
        );
      } catch (err) {
        if (err instanceof SupersededError) throw err;
        return failWith(`Cannot reach backend: ${(err as Error).message}`);
      }

      if (resp.status === 401) {
        const { signOut } = useAuth();
        await signOut();
        throw new SessionExpiredError();
      }

      // Try the YOUTUBE_DUPLICATE intercept BEFORE the generic
      // !resp.ok path so the body is still readable. parseJson handles
      // a malformed body (e.g. a 409 emitted by a future endpoint
      // that doesn't carry the dedupe pointer) by returning ok:false,
      // and we fall through to the generic-error handler.
      if (resp.status === 409) {
        const parsed = await guardedAwait(
          parseJson<{
            code?: unknown;
            existing_job_id?: unknown;
            existing_job_name?: unknown;
            existing_created_at?: unknown;
          }>(resp, "POST /jobs (409)"),
        );
        if (
          parsed.ok &&
          parsed.data.code === "YOUTUBE_DUPLICATE" &&
          typeof parsed.data.existing_job_id === "string" &&
          typeof parsed.data.existing_job_name === "string" &&
          typeof parsed.data.existing_created_at === "string"
        ) {
          // Reset state so the form is re-submittable from the modal's
          // "Run again" path without an intervening reset() call.
          state.value = "idle";
          return {
            ok: false,
            // Placeholder fallback message; NewJobForm prefers the
            // modal renderer and never displays this string.
            error: "Already analyzed",
            duplicate: {
              existingJobId: parsed.data.existing_job_id,
              existingJobName: parsed.data.existing_job_name,
              existingCreatedAt: parsed.data.existing_created_at,
            },
          };
        }
        // 409 without a dedupe pointer (or with malformed JSON) — fall
        // through to a generic error. The body is consumed, so we can't
        // call readUserFacingError; render a status-based message.
        return failWith(genericNetworkError(resp.status));
      }

      if (!resp.ok) {
        const ufe = await readUserFacingError(resp);
        return failWith(ufe.message, ufe.traceId);
      }

      const createJson = await guardedAwait(
        parseJson<{ job_id?: unknown }>(resp, "POST /jobs"),
      );
      if (!createJson.ok) return failWith(createJson.message);
      const newJobId = createJson.data.job_id;
      if (typeof newJobId !== "string" || newJobId.length === 0) {
        return failWith("Backend response from POST /jobs is missing job_id.");
      }
      jobId.value = newJobId;
      state.value = "completed";
      return { ok: true, jobId: newJobId };
    } catch (err) {
      if (err instanceof SupersededError) {
        return { ok: false, error: "Superseded" };
      }
      if (err instanceof SessionExpiredError) {
        return { ok: false, error: "Session expired" };
      }
      throw err;
    }
  }

  /**
   * User-initiated cancellation. Works in ANY phase — not just during
   * the actual PUT to B2.
   *
   * Bumps `currentGen` (supersedes any in-flight start) and directly
   * transitions to the `cancelled` terminal state. The in-flight start
   * will see SupersededError at its next guardedAwait and return without
   * touching the refs (so this set wins).
   *
   * If an XHR is in flight (state === "uploading"), aborts it too;
   * the resulting UploadCancelledError is swallowed by the outer
   * try/catch via the SupersededError path.
   */
  function cancel(): void {
    currentGen++;
    if (activeUpload) {
      activeUpload.abort();
    }
    state.value = "cancelled";
    error.value = null;
    activeUpload = null;
  }

  return {
    state,
    progress,
    bytesSent,
    totalBytes,
    bytesPerSec,
    error,
    errorTraceId,
    jobId,
    start,
    startWithUrl,
    cancel,
    reset,
  };
}
